import React from 'react';

import Draw from 'ol/interaction/Draw';
import Map from 'ol/Map';
import PropTypes from 'prop-types';
import View from 'ol/View';
import { fromCircle } from 'ol/geom/Polygon';
import { fromLonLat, toLonLat } from 'ol/proj';
import { OSM, Vector as VectorSource } from 'ol/source';
import { Tile as TileLayer } from 'ol/layer';
import { useSelector } from 'react-redux';
import { Button } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import KML from 'ol/format/KML';

import Icons from 'consts/icons';
import { addLayerToMap, getGeojsonGeometryFromFeature } from 'utils/map/map';
import {
  addFeaturesToLayer,
  addFeaturesToLayerUsingGeojsonGeometry,
  clearMapLayer,
  createLayer,
  createVectorSourceUsingGeojsonGeometry,
} from 'utils/map/layers';
import { getJurisdictionExtent } from 'components/map/mapUtils';
import Tooltip from '@material-ui/core/Tooltip';
import ButtonsUseStyles from 'consts/styles/Buttons';
import { formStyles } from 'consts/styles/Form';
import { mapUseStyles } from 'components/common/components/drawMap/mapUseStyles';
import { useStyles } from './DrawShapeUseStyles';

const DrawShape = props => {
  const {
    onGeometryChange,
    drawnGeometry,
    editing,
    category,
    setIsDrawing,
  } = props;
  const map = React.useRef({});

  const [active, setActive] = React.useState(null);

  const classes = useStyles();
  const mapClasses = mapUseStyles();
  const formClasses = formStyles();
  const classesButtons = ButtonsUseStyles();

  const drawingsLayer = React.useRef(null);
  const tileLayer = React.useRef(
    new TileLayer({
      source: new OSM(),
    })
  );
  const inputFile = React.useRef('');
  const draw = React.useRef(null);
  const [error, setError] = React.useState(true);
  const [fileName, setFileName] = React.useState(null);
  const useString = () => {
    const language = useSelector(state => state.language);
    return language.adminZone.addOrganization.iconsLabels;
  };
  const subtitles = useString();

  React.useEffect(() => {
    drawingsLayer.current = createLayer('drawingsLayer');
    map.current = new Map({
      layers: [tileLayer.current],
      target: 'mapToDraw',
      view: new View({
        center: fromLonLat([19.015839, 52.2307]),
        zoom: 10,
      }),
    });
    const zoneExtent = drawnGeometry
      ? createVectorSourceUsingGeojsonGeometry(drawnGeometry)?.getExtent()
      : getJurisdictionExtent();
    addLayerToMap(map.current, drawingsLayer.current);
    if (!!zoneExtent && Number.isFinite(zoneExtent[0])) {
      map.current.getView().fit(zoneExtent);
    } else {
      map.current.getView().fit(getJurisdictionExtent());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initDrawShape = () => {
    if (!drawnGeometry) setError(false);
    if (!drawnGeometry) return;
    addFeaturesToLayerUsingGeojsonGeometry(
      drawingsLayer.current,
      drawnGeometry
    );
  };

  React.useEffect(initDrawShape, []);
  React.useEffect(() => {
    setIsDrawing(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const addInteraction = typeValue => {
    setActive(typeValue);
    map.current.removeInteraction(draw.current);
    clearMapLayer(drawingsLayer.current);
    if (typeValue !== 'None') {
      setError(true);
      draw.current = new Draw({
        source: new VectorSource(),
        type: typeValue,
      });
      map.current.addInteraction(draw.current);

      draw.current.on('drawend', function(event) {
        drawEnded(event.feature, typeValue);
      });
    } else {
      onGeometryChange({});
      setError(false);
    }
  };

  const drawEnded = (feature, typeValue) => {
    if (typeValue === 'Circle') {
      addFeaturesToLayer(drawingsLayer.current, [feature]);
      map.current.removeInteraction(draw.current);
      const circleAsPolygon = fromCircle(feature.getGeometry(), 64, 0);
      const circleGeojsonObject = {
        type: 'Polygon',
        coordinates: [
          parseFlatCoordinatesForCircle(circleAsPolygon.flatCoordinates),
        ],
      };
      onGeometryChange(circleGeojsonObject);
    } else {
      addFeaturesToLayer(drawingsLayer.current, [feature]);
      map.current.removeInteraction(draw.current);
      onGeometryChange(getGeojsonGeometryFromFeature(feature));
    }
  };

  const parseFlatCoordinatesForCircle = circleArr => {
    const returnedArray = [];
    for (let i = 0; i < circleArr.length; i += 1) {
      if (i < circleArr.length - 1) {
        const firstElem = circleArr[i];
        const secondElem = circleArr[i + 1];
        const eachArr = toLonLat([firstElem, secondElem], 'EPSG:3857');
        returnedArray.push(eachArr);
        i += 1;
      }
    }

    return returnedArray;
  };

  const uploadFile = e => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file?.name);
      const reader = new FileReader();
      reader.onload = () => {
        const shapeSource = new VectorSource({
          url: reader.result,
          format: new KML(),
        });

        drawingsLayer.current.setSource(shapeSource);
        shapeSource.on('change', evt => {
          const source = evt.target;
          if (source.getState() === 'ready') {
            const renderedFeatures = source.getFeatures();
            onGeometryChange(
              getGeojsonGeometryFromFeature(renderedFeatures[0])
            );
            setError(true);
          }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    inputFile.current.value = '';
    setFileName(null);
  };

  return (
    <div className={`${classes.DrawShape}__map__wrapper__outer`}>
      <label htmlFor="readFileInput">
        <input
          className={`${classes.DrawShape}__map__wrapper__fileInput`}
          type="file"
          onChange={e => uploadFile(e)}
          id="readFileInput"
          ref={inputFile}
          accept=".kml"
          disabled={editing}
        />
        <Button
          variant="contained"
          component="span"
          disabled={editing}
          className={`${classesButtons.Buttons} ${classesButtons.Buttons}--color ${classesButtons.ButtonsRectangle}`}
        >
          {subtitles.selectFile_}
        </Button>

        {fileName === null ? (
          <span className={`${formClasses.Form}__text-field`}>
            {subtitles.fileFormat_}
          </span>
        ) : (
          <span className={`${formClasses.Form}__text-field`}>
            {fileName}
            <IconButton
              aria-label="delete"
              color="secondary"
              onClick={handleRemoveFile}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        )}
      </label>

      {!editing && (
        <div className={`${mapClasses.DrawingApprovedAreasMap}__icons-wrapper`}>
          <Tooltip title={subtitles.polygon_} placement="right">
            <Icons.Actions.Edit2
              className={`${mapClasses.DrawingApprovedAreasMap}__map-icon ${
                active === 'Polygon'
                  ? `${mapClasses.DrawingApprovedAreasMap}__map-icon--active`
                  : ''
              } ${classesButtons.ButtonsCircle}--mid`}
              onClick={() => addInteraction('Polygon')}
            />
          </Tooltip>

          {category === 'corridor' && (
            <Tooltip title={subtitles.line_} placement="right">
              <Icons.Misc.Timeline
                className={`${mapClasses.DrawingApprovedAreasMap}__map-icon ${
                  active === 'LineString'
                    ? `${mapClasses.DrawingApprovedAreasMap}__map-icon--active`
                    : ''
                } ${classesButtons.ButtonsCircle}--mid`}
                onClick={() => addInteraction('LineString')}
              />
            </Tooltip>
          )}
          <Tooltip title={subtitles.circle_} placement="right">
            <Icons.Misc.RadioUnchecked
              className={`${mapClasses.DrawingApprovedAreasMap}__map-icon ${
                active === 'Circle'
                  ? `${mapClasses.DrawingApprovedAreasMap}__map-icon--active`
                  : ''
              } ${classesButtons.ButtonsCircle}--mid`}
              onClick={() => addInteraction('Circle')}
            />
          </Tooltip>

          <Tooltip title={subtitles.clear_} placement="right">
            <Icons.Actions.HighlightOff
              className={`${mapClasses.DrawingApprovedAreasMap}__map-icon
               ${classesButtons.ButtonsCircle}--mid`}
              onClick={() => addInteraction('None')}
            />
          </Tooltip>
        </div>
      )}
      <div
        className={`${classes.DrawShape}__map__wrapper__inner--height`}
        id="mapToDraw"
      ></div>
    </div>
  );
};

DrawShape.propTypes = {
  onGeometryChange: PropTypes.func.isRequired,
  drawnGeometry: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.instanceOf(null).isRequired,
  ]),
  editing: PropTypes.bool.isRequired,
  category: PropTypes.string,
  setIsDrawing: PropTypes.func.isRequired,
};

export default DrawShape;
